import ResetError from '@/routes/reset-password/-components/resetError.tsx'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { useAtom } from 'jotai/index'
import { ReactNode, useEffect } from 'react'

import { useGetForgotPasswordTokenFundamentals } from '@/gen/publicApi'

import Logo from '@/containers/logo'

import Spinner from '@/components/spinner'
import { ModeToggle } from '@/components/theme/mode-toggle.tsx'

import { authTokenAtom } from '@/state/authToken.tsx'
import { useIdentity } from '@/state/identity.tsx'

import ResetPasswordForm from './-components/reset-password-form.tsx'

export const Route = createFileRoute('/reset-password/$token')({
    component: ResetPasswordComp,
})

function ResetPasswordComp() {
    const forgotPasswordToken = Route.useParams().token
    const { data: tokenData } = useGetForgotPasswordTokenFundamentals(forgotPasswordToken)

    const [authToken] = useAtom(authTokenAtom)
    const { identity } = useIdentity()
    const router = useRouter()

    useEffect(() => {
        if (identity) {
            // Hard refresh redirect on login do avoid potential state issues
            location.href = '/'
        }
    }, [identity, router.history])

    if (!tokenData || identity || authToken) {
        return <Spinner size="md" absolute />
    }

    const expiryDate = new Date(tokenData.expiresUtc)

    let cmp: ReactNode
    if (tokenData.tokenState === 'Accepted') {
        cmp = (
            <ResetError
                title="Already used password reset"
                errorMessage="This password reset token has already been used. If you wish to reset your password again, please request a new password reset email."
            />
        )
    } else if (tokenData.tokenState === 'Revoked') {
        cmp = (
            <ResetError
                title="Revoked password reset"
                errorMessage="This password reset has been revoked. If you wish to reset your password again, please request a new password reset email."
            />
        )
    } else if (tokenData.tokenState === 'Expired' || expiryDate < new Date()) {
        cmp = (
            <ResetError
                title="Password reset expired"
                errorMessage="This password reset token has expired. If you wish to reset your password again, please request a new password reset email."
            />
        )
    } else {
        cmp = <ResetPasswordForm forgotPasswordToken={forgotPasswordToken} />
    }

    return (
        <div className="relative grid h-screen flex-col items-center justify-center px-4 lg:max-w-none lg:grid-cols-2 lg:px-0">
            <div className="absolute right-8 top-8">
                <ModeToggle />
            </div>
            <div className="mx-auto hidden h-full w-full flex-col items-center justify-center bg-muted lg:flex">
                <Logo className="mb-4 mr-2" />
                <h1 className="text-2xl font-semibold tracking-tight">Moxie Markets</h1>
            </div>
            <div className="lg:p-8">
                <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
                    <div className="visible mx-auto mb-12 flex w-full flex-col items-center justify-center text-center lg:hidden">
                        <Logo className="mb-4 mr-2" />
                        <h1 className="text-2xl font-semibold tracking-tight">Moxie Markets</h1>
                    </div>
                    {cmp}
                </div>
            </div>
        </div>
    )
}
